<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  fetchChatContacts,
  fetchChatMessageById,
  fetchChatMessages,
  fetchChatMessagesTranslation,
  fetchUserRelations,
  getMediaUrl,
  fetchUsers,
  fetchUserById,
} from "@/services/backofficeClient";

const CONTACT_PAGE_SIZE = 15;
const MESSAGE_PAGE_SIZE = 30;
const TRANSLATION_MESSAGE_LIMIT = 30;
const TRANSLATION_LANGUAGES = [
  { code: "English", label: "English" },
  { code: "Persian", label: "Persian" },
];
const RELATION_TYPE_COLORS = {
  like: "#2fb344",
  super_like: "#0ca678",
  dislike: "#f76707",
  block: "#d63939",
  direct_message: "#17a2b8",
  messaged: "#6c757d",
};

const route = useRoute();
const router = useRouter();

const contacts = ref([]);
const contactPagination = ref({
  page: 1,
  pageSize: CONTACT_PAGE_SIZE,
  totalItems: 0,
  totalPages: 1,
  hasNext: false,
});
const contactsLoading = ref(true);
const contactsError = ref("");

const messages = ref([]);
const messagePagination = ref({
  page: 1,
  pageSize: MESSAGE_PAGE_SIZE,
  totalItems: 0,
  totalPages: 1,
  hasNext: false,
});
const messagesLoading = ref(false);
const messagesError = ref("");
const currentMessagePage = ref(1);

const userMap = reactive({});
const selectedContactKey = ref("");
const messageContainer = ref(null);
const selectedParticipantIds = ref([]);
const participantIdsInput = ref("");
const highlightedMessageId = ref("");
let highlightTimeout = null;
const participantSuggestions = ref([]);
const participantSuggestionsLoading = ref(false);
let participantSearchTimeout = null;

const contactRelations = ref([]);
const relationsLoading = ref(false);

const translateMenuOpen = ref(false);
const translateLoading = ref(false);
const translateError = ref("");
const activeTranslationLanguage = ref("");
const translationsByLanguage = reactive({});

const selectedContact = computed(() =>
  contacts.value.find((c) => c.key === selectedContactKey.value) || null
);
const messageById = computed(() => {
  const map = {};
  messages.value.forEach((message) => {
    if (message.id) {
      map[message.id] = message;
    }
  });
  return map;
});

const resolveMediaKind = (mediaPath) => {
  if (!mediaPath) return null;
  const lower = mediaPath.toString().toLowerCase();
  if (lower.includes("photo")) return "photo";
  if (lower.includes("voice")) return "voice";
  return "media";
};

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const firstPhotoPath = (user) => {
  const atoms = Array.isArray(user?.atoms) ? user.atoms : [];
  const photoAtom = atoms.find(
    (atom) =>
      atom &&
      (atom.attribute === "photo" ||
        atom.attribute === "main_photo" ||
        atom.attribute === "avatar") &&
      (atom.mediaPath || atom.media_path)
  );
  return photoAtom?.mediaPath || photoAtom?.media_path || null;
};

const resolveUserName = (id) => {
  const user = id ? userMap[id] : null;
  if (!user) return "Unknown";
  return (
    user.name ||
    user.fullName ||
    user.displayName ||
    [user.firstName, user.lastName]
      .filter(Boolean)
      .map((p) => `${p}`)
      .join(" ")
      .trim() ||
    "Unknown"
  );
};

const parseUser = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id || raw._id || raw.userId;
  if (!id) return null;
  return {
    id: `${id}`,
    name: raw.name || raw.fullName || raw.displayName || raw.username || "User",
    status: raw.status || null,
    email: raw.email || null,
    phone: raw.phone || null,
    atoms: Array.isArray(raw.atoms) ? raw.atoms : [],
  };
};

const upsertUsers = (list) => {
  if (!Array.isArray(list)) return;
  list.forEach((item) => {
    const parsed = parseUser(item);
    if (!parsed) return;
    userMap[parsed.id] = { ...(userMap[parsed.id] || {}), ...parsed };
  });
};

const normalizeIdList = (value) => {
  if (!value) return [];
  return value
    .split(/[\s,]+/)
    .map((v) => (v && typeof v === "string" ? v.trim() : `${v}`.trim()))
    .filter(Boolean);
};

const parseParticipantQuery = () => {
  const ids = new Set();
  const q = route.query || {};
  const rawArray = q["participantIds[]"];
  const arrayValues = Array.isArray(rawArray)
    ? rawArray
    : rawArray
      ? [rawArray]
      : [];
  arrayValues.forEach((v) => {
    const trimmed = typeof v === "string" ? v.trim() : `${v}`.trim();
    if (trimmed) ids.add(trimmed);
  });

  if (q.participantIds) {
    normalizeIdList(q.participantIds).forEach((v) => ids.add(v));
  }
  return Array.from(ids);
};

const syncParticipantFilterFromRoute = () => {
  const ids = parseParticipantQuery();
  selectedParticipantIds.value = ids;
  participantIdsInput.value = ids.join(", ");
};

const updateRouteWithParticipants = () => {
  const ids = selectedParticipantIds.value;
  const newQuery = { ...route.query };
  delete newQuery["participantIds[]"];
  delete newQuery.participantIds;
  delete newQuery.page;
  delete newQuery.messageId;
  if (ids.length > 0) {
    newQuery["participantIds[]"] = ids;
  }
  router.replace({ query: newQuery }).catch(() => { });
};

const addParticipantIds = (ids) => {
  const normalized = normalizeIdList(ids.join ? ids.join(",") : ids);
  let changed = false;
  normalized.forEach((id) => {
    if (!selectedParticipantIds.value.includes(id)) {
      selectedParticipantIds.value.push(id);
      changed = true;
    }
  });
  if (changed) {
    participantIdsInput.value = selectedParticipantIds.value.join(", ");
    updateRouteWithParticipants();
  }
};

const removeParticipantId = (id) => {
  const next = selectedParticipantIds.value.filter((v) => v !== id);
  selectedParticipantIds.value = next;
  participantIdsInput.value = next.join(", ");
  updateRouteWithParticipants();
};

const addParticipantsFromInput = () => {
  const ids = normalizeIdList(participantIdsInput.value);
  if (ids.length === 0) return;
  addParticipantIds(ids);
};

const loadParticipantSuggestions = async (searchTerm) => {
  const trimmed = typeof searchTerm === "string" ? searchTerm.trim() : "";
  if (!trimmed || trimmed.length < 2) {
    participantSuggestions.value = [];
    return;
  }
  participantSuggestionsLoading.value = true;
  try {
    const payload = await fetchUsers({
      search: trimmed,
      page: 1,
      pageSize: 5,
    });
    const rawItems = Array.isArray(payload) ? payload : payload?.items;
    const parsed = Array.isArray(rawItems)
      ? rawItems
        .filter((item) => item && typeof item === "object")
        .map((item) => parseUser(item))
        .filter(Boolean)
      : [];
    participantSuggestions.value = parsed;
  } catch (error) {
    console.error("Failed to load participant suggestions", error);
    participantSuggestions.value = [];
  } finally {
    participantSuggestionsLoading.value = false;
  }
};

const debounceParticipantSuggestions = (value) => {
  if (participantSearchTimeout) {
    clearTimeout(participantSearchTimeout);
  }
  participantSearchTimeout = setTimeout(
    () => loadParticipantSuggestions(value),
    250
  );
};

const selectParticipantSuggestion = (user) => {
  if (!user?.id) return;
  addParticipantIds([user.id]);
  participantSuggestions.value = [];
  participantIdsInput.value = "";
};

const hydrateSelectedParticipants = async () => {
  const ids = [...selectedParticipantIds.value];
  if (ids.length === 0) return;
  try {
    const results = await Promise.allSettled(
      ids.map((id) =>
        fetchUserById(id)
          .then((user) => parseUser(user))
          .catch(() => null)
      )
    );
    const parsed = results
      .filter((r) => r.status === "fulfilled" && r.value)
      .map((r) => r.value);
    upsertUsers(parsed);
  } catch (error) {
    console.error("Failed to hydrate participants from route", error);
  }
};

const describeMessageContent = (message) => {
  if (!message) return "";
  if (message.text) return message.text;
  if (message.mediaKind === "photo") return "[Photo]";
  if (message.mediaKind === "voice") return "[Voice message]";
  if (message.mediaKind === "media") return "[Media]";
  return "";
};

const parseMessage = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id || raw._id;
  if (!id) return null;
  const fromUser = parseUser(raw.fromUser || raw.from_user);
  if (fromUser) {
    upsertUsers([fromUser]);
  }
  if (raw.users) {
    upsertUsers(raw.users);
  }
  const replyToAtomRaw = raw.replyToAtom || raw.reply_to_atom;
  const replyToMessage = raw.replyToMessage || raw.reply_to_message || null;
  const createdAtRaw = raw.createdAt || raw.created_at;
  const createdAtDate = createdAtRaw ? new Date(createdAtRaw) : null;
  const replyAtomUser = parseUser(replyToAtomRaw?.user);
  if (replyAtomUser) {
    upsertUsers([replyAtomUser]);
  }
  const participants =
    raw.participants && Array.isArray(raw.participants)
      ? raw.participants.map((p) => `${p}`)
      : [];

  const deletedAtRaw = raw.deletedAt || raw.deleted_at;
  const parseUserActions = (list) =>
    Array.isArray(list)
      ? list.map((action) => ({
        userId: `${action.userId || action.user_id || ""}`,
        dateTime: action.dateTime || action.date_time || null,
        dateTimeLabel: formatTimestamp(action.dateTime || action.date_time),
      }))
      : [];

  return {
    id: `${id}`,
    idForClient: raw.idForClient || raw.id_for_client || null,
    participants,
    fromUserId:
      raw.fromUserId ||
      raw.from_user_id ||
      raw.fromUser ||
      raw.from_user ||
      fromUser?.id ||
      null,
    text: raw.text || raw.message || "",
    mediaPath: raw.mediaPath || raw.media || null,
    mediaKind: resolveMediaKind(raw.mediaPath || raw.media),
    replyToMessage,
    replyToAtom: replyToAtomRaw
      ? {
        id: replyToAtomRaw.id,
        attribute: replyToAtomRaw.attribute,
        userId:
          replyToAtomRaw.userId ||
          replyToAtomRaw.user_id ||
          replyToAtomRaw.user?.id ||
          null,
      }
      : null,
    createdAt: createdAtDate && !Number.isNaN(createdAtDate.getTime())
      ? createdAtDate
      : null,
    createdAtLabel: formatTimestamp(createdAtRaw),
    deletedAt: deletedAtRaw || null,
    deletedAtLabel: formatTimestamp(deletedAtRaw),
    receivedBy: parseUserActions(raw.receivedBy || raw.received_by),
    seenBy: parseUserActions(raw.seenBy || raw.seen_by),
  };
};

const parseContact = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const participants = Array.isArray(raw.participants)
    ? raw.participants.map((p) => `${p}`)
    : [];
  if (participants.length !== 2) return null;
  const key = participants.join("-");
  const lastMessage = raw.lastMessage ? parseMessage(raw.lastMessage) : null;
  return {
    key,
    participants,
    count: Number(raw.count) || 0,
    lastMessage,
    lastMessagePreview: describeMessageContent(lastMessage) || "No messages",
  };
};

const formatRelationType = (value) => {
  if (!value) return "";
  return String(value).replace(/_/g, " ");
};

const relationTypeBadgeStyle = (type) => {
  const color = RELATION_TYPE_COLORS[type] || "#6c757d";
  return {
    background: color,
    color: "#fff",
  };
};

const parseRelation = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const fromUserId = raw.fromUserId || raw.from_user_id;
  const toUserId = raw.toUserId || raw.to_user_id;
  const type = raw.type || null;
  if (!fromUserId || !toUserId || !type) return null;
  return {
    fromUserId: `${fromUserId}`,
    toUserId: `${toUserId}`,
    type,
  };
};

const loadContactRelations = async (participants) => {
  if (!Array.isArray(participants) || participants.length !== 2) {
    contactRelations.value = [];
    return;
  }
  relationsLoading.value = true;
  try {
    const payload = await fetchUserRelations({
      userIds: participants,
      pageSize: 50,
    });
    const rawItems = Array.isArray(payload) ? payload : payload?.items;
    contactRelations.value = Array.isArray(rawItems)
      ? rawItems.map((item) => parseRelation(item)).filter(Boolean)
      : [];
  } catch (error) {
    console.error("Failed to load participant relations", error);
    contactRelations.value = [];
  } finally {
    relationsLoading.value = false;
  }
};

const resolvePagination = (rawPagination, itemsLength, defaultPageSize, page) => {
  const fallbackTotalPages = Math.max(
    1,
    Math.ceil((itemsLength || 0) / defaultPageSize)
  );
  if (rawPagination && typeof rawPagination === "object") {
    const rawPage = Number(rawPagination.page) || page || 1;
    const rawPageSize =
      Number(rawPagination.pageSize) || Number(rawPagination.page_size);
    const pageSize =
      rawPageSize && rawPageSize > 0 ? rawPageSize : defaultPageSize;
    const totalItemsValue =
      Number(rawPagination.totalItems) ||
      Number(rawPagination.total_items) ||
      Number(itemsLength) ||
      0;
    const totalPagesValue =
      Number(rawPagination.totalPages) ||
      Number(rawPagination.total_pages) ||
      fallbackTotalPages;
    const hasNext =
      rawPagination.hasNext === true
        ? true
        : rawPagination.has_next === true
          ? true
          : rawPage < (totalPagesValue || fallbackTotalPages);
    return {
      page: rawPage > 0 ? rawPage : 1,
      pageSize,
      totalItems: totalItemsValue >= 0 ? totalItemsValue : itemsLength,
      totalPages: totalPagesValue > 0 ? totalPagesValue : fallbackTotalPages,
      hasNext,
    };
  }
  return {
    page: page || 1,
    pageSize: defaultPageSize,
    totalItems: itemsLength,
    totalPages: fallbackTotalPages,
    hasNext: fallbackTotalPages > (page || 1),
  };
};

const mergeMessages = (incoming) => {
  const map = new Map();
  messages.value.forEach((message) => {
    map.set(message.id, message);
  });
  incoming.forEach((message) => {
    map.set(message.id, message);
  });
  const merged = Array.from(map.values()).sort((a, b) => {
    const aTime = a.createdAt ? a.createdAt.getTime() : 0;
    const bTime = b.createdAt ? b.createdAt.getTime() : 0;
    if (aTime === bTime) {
      return a.id.localeCompare(b.id);
    }
    return aTime - bTime;
  });
  messages.value = merged;
};

const captureScrollSnapshot = () => {
  const el = messageContainer.value;
  if (!el) return null;
  return {
    scrollHeight: el.scrollHeight,
    scrollTop: el.scrollTop,
  };
};

const restoreScrollSnapshot = (snapshot) => {
  if (!snapshot) return;
  const el = messageContainer.value;
  if (!el) return;
  const delta = el.scrollHeight - snapshot.scrollHeight;
  el.scrollTop = snapshot.scrollTop + delta;
};

const scrollToBottom = () => {
  nextTick(() => {
    const el = messageContainer.value;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  });
};

const loadContacts = async (page = 1) => {
  contactsLoading.value = true;
  contactsError.value = "";
  try {
    const payload = await fetchChatContacts({
      page,
      pageSize: CONTACT_PAGE_SIZE,
      participantIds: selectedParticipantIds.value,
    });
    const usersFromPayload = Array.isArray(payload?.users)
      ? payload.users
      : [];
    upsertUsers(usersFromPayload);
    const rawItems = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
        ? payload.items
        : [];
    const parsed = rawItems
      .map((item) => parseContact(item))
      .filter(Boolean);
    contactPagination.value = resolvePagination(
      payload?.pagination,
      parsed.length,
      CONTACT_PAGE_SIZE,
      page
    );
    contacts.value = parsed;
    const hasSelected = parsed.some(
      (contact) => contact.key === selectedContactKey.value
    );
    if ((!selectedContactKey.value || !hasSelected) && parsed.length > 0) {
      selectedContactKey.value = parsed[0].key;
      await nextTick();
      await loadMessages(1, true);
      await loadContactRelations(parsed[0].participants);
    } else if (parsed.length === 0) {
      selectedContactKey.value = "";
      messages.value = [];
      contactRelations.value = [];
    }
  } catch (error) {
    console.error("Failed to load contacts", error);
    contactsError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load contacts.";
  } finally {
    contactsLoading.value = false;
  }
};

const loadMessages = async (page = 1, reset = false) => {
  if (!selectedContact.value) return;
  const participants = selectedContact.value.participants;
  messagesLoading.value = true;
  messagesError.value = "";
  const snapshot = page > 1 ? captureScrollSnapshot() : null;
  if (reset) {
    messages.value = [];
    currentMessagePage.value = 1;
  }
  try {
    const payload = await fetchChatMessages({
      participantIds: participants,
      page,
      pageSize: MESSAGE_PAGE_SIZE,
    });
    const usersFromPayload = Array.isArray(payload?.users)
      ? payload.users
      : [];
    upsertUsers(usersFromPayload);
    const rawItems = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
        ? payload.items
        : [];
    const parsed = rawItems.map((item) => parseMessage(item)).filter(Boolean);
    mergeMessages(parsed);
    messagePagination.value = resolvePagination(
      payload?.pagination,
      messages.value.length,
      MESSAGE_PAGE_SIZE,
      page
    );
    currentMessagePage.value = page;
    if (page === 1) {
      scrollToBottom();
    } else {
      nextTick(() => restoreScrollSnapshot(snapshot));
    }
  } catch (error) {
    console.error("Failed to load messages", error);
    messagesError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load messages.";
  } finally {
    messagesLoading.value = false;
  }
};

const loadOlderMessages = () => {
  if (messagesLoading.value || !messagePagination.value.hasNext) return;
  loadMessages(currentMessagePage.value + 1, false);
};

const parseMessageIdFromRoute = () => {
  const raw = route.query?.messageId;
  const value = Array.isArray(raw) ? raw[0] : raw;
  return value ? String(value).trim() : "";
};

const resolveMessageParticipants = async (messageId) => {
  try {
    const payload = await fetchChatMessageById(messageId);
    const usersFromPayload = Array.isArray(payload?.users)
      ? payload.users
      : [];
    upsertUsers(usersFromPayload);
    const message = parseMessage(payload?.message);
    if (!message || message.participants.length !== 2) return null;
    return message.participants;
  } catch (error) {
    console.error("Failed to resolve message for deep link", error);
    return null;
  }
};

const highlightMessageElement = (messageId) => {
  const el = messageContainer.value?.querySelector(
    `[data-message-id="${messageId}"]`
  );
  if (!el) return false;
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  highlightedMessageId.value = messageId;
  if (highlightTimeout) clearTimeout(highlightTimeout);
  highlightTimeout = setTimeout(() => {
    highlightedMessageId.value = "";
  }, 2500);
  return true;
};

const locateAndHighlightMessage = async (messageId) => {
  if (!messageId) return;
  await nextTick();
  if (highlightMessageElement(messageId)) return;
  while (messagePagination.value.hasNext) {
    await loadMessages(currentMessagePage.value + 1, false);
    await nextTick();
    if (highlightMessageElement(messageId)) return;
  }
};

const selectContact = async (contact) => {
  if (!contact) return;
  selectedContactKey.value = contact.key;
  await nextTick();
  await loadMessages(1, true);
  await loadContactRelations(contact.participants);
};

const contactName = (contact, index) => {
  if (!contact) return "Unknown";
  const userId = contact.participants[index];
  return resolveUserName(userId);
};

const userProfileUrl = (userId) => {
  const id = userId ? String(userId) : "";
  return id ? `/users/${id}` : null;
};

const contactPhotoUrl = (contact, index) => {
  if (!contact) return null;
  const userId = contact.participants[index];
  const user = userMap[userId];
  const photoPath = firstPhotoPath(user);
  return photoPath ? getMediaUrl(photoPath) : null;
};

const participantLabel = computed(() => {
  if (!selectedContact.value) return "Select a chat";
  const [first, second] = selectedContact.value.participants;
  return `${resolveUserName(first)} • ${resolveUserName(second)}`;
});

const relationBadges = computed(() => {
  if (!selectedContact.value) return [];
  return contactRelations.value.map((relation) => ({
    key: `${relation.fromUserId}-${relation.toUserId}-${relation.type}`,
    type: relation.type,
    label: `${resolveUserName(relation.fromUserId)} → ${resolveUserName(
      relation.toUserId
    )}: ${formatRelationType(relation.type)}`,
  }));
});

const isFromUserA = (message) => {
  if (!selectedContact.value || !message) return false;
  return message.fromUserId === selectedContact.value.participants[0];
};

const recipientId = (message) => {
  if (!message) return null;
  return message.participants.find((id) => id !== message.fromUserId) || null;
};

const deliveredInfo = (message) => {
  const toId = recipientId(message);
  const action = message?.receivedBy?.find((a) => a.userId === toId);
  return action?.dateTimeLabel ? action : null;
};

const seenInfo = (message) => {
  const toId = recipientId(message);
  const action = message?.seenBy?.find((a) => a.userId === toId);
  return action?.dateTimeLabel ? action : null;
};

const replyToMessageSnippet = (message) => {
  if (!message?.replyToMessage) return null;
  const target = messageById.value[message.replyToMessage];
  if (target) {
    return describeMessageContent(target) || `Message ${target.id}`;
  }
  return `Message ${message.replyToMessage}`;
};

const latestTextMessages = computed(() =>
  messages.value
    .filter((message) => !message.mediaKind && message.text && message.text.trim())
    .slice(-TRANSLATION_MESSAGE_LIMIT)
);

const toggleTranslateMenu = () => {
  translateMenuOpen.value = !translateMenuOpen.value;
};

const translationFor = (message) => {
  if (!activeTranslationLanguage.value || !message) return null;
  const cache = translationsByLanguage[activeTranslationLanguage.value];
  return cache ? cache[message.id] || null : null;
};

const translateChat = async (language) => {
  translateMenuOpen.value = false;
  translateError.value = "";
  activeTranslationLanguage.value = language;

  const cache =
    translationsByLanguage[language] || (translationsByLanguage[language] = {});
  const pendingMessages = latestTextMessages.value.filter(
    (message) => !(message.id in cache)
  );
  if (pendingMessages.length === 0) return;

  translateLoading.value = true;
  try {
    const payload = await fetchChatMessagesTranslation(
      pendingMessages.map((message) => message.id),
      language
    );
    const translations = Array.isArray(payload?.translations)
      ? payload.translations
      : [];
    translations.forEach((translation) => {
      if (translation?.messageId) {
        cache[translation.messageId] = translation.translatedText;
      }
    });
  } catch (error) {
    console.error("Failed to translate messages", error);
    translateError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to translate messages.";
  } finally {
    translateLoading.value = false;
  }
};

syncParticipantFilterFromRoute();

const refreshFromRoute = async () => {
  syncParticipantFilterFromRoute();
  const messageId = parseMessageIdFromRoute();
  if (messageId) {
    const participants = await resolveMessageParticipants(messageId);
    if (participants) {
      selectedParticipantIds.value = participants;
      participantIdsInput.value = participants.join(", ");
    }
  }
  await hydrateSelectedParticipants();
  await loadContacts();
  if (messageId) {
    await locateAndHighlightMessage(messageId);
  }
};

onMounted(refreshFromRoute);

watch(
  () => route.query,
  (newQuery, oldQuery) => {
    if (newQuery === oldQuery) return;
    refreshFromRoute();
  }
);
</script>

<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-lg-4">
        <div class="card h-100">
          <div class="card-header">
            <div class="border-bottom">
              <label class="form-label text-sm fw-semibold">Filter by participant IDs</label>
              <div class="position-relative">
                <input v-model="participantIdsInput" type="text" class="form-control form-control-sm"
                  placeholder="Type name, email, or user ID"
                  @input="debounceParticipantSuggestions(participantIdsInput)"
                  @keyup.enter.prevent="addParticipantsFromInput" />
                <div v-if="participantSuggestions.length" class="list-group suggestion-dropdown">
                  <button v-for="suggestion in participantSuggestions" :key="suggestion.id" type="button"
                    class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    @click="selectParticipantSuggestion(suggestion)">
                    <div>
                      <div class="fw-semibold text-sm">{{ suggestion.name }}</div>
                      <div class="text-xs text-muted">{{ suggestion.id }}</div>
                    </div>
                    <span class="badge bg-gradient-info">Add</span>
                  </button>
                </div>
              </div>
              <div class="d-flex flex-wrap gap-2 mt-2" v-if="selectedParticipantIds.length">
                <span v-for="id in selectedParticipantIds" :key="id" class="badge bg-light text-dark border">
                  {{ resolveUserName(id) }} ({{ id }})
                  <button type="button" class="btn btn-link text-danger text-xxs ms-1 p-0 align-baseline"
                    @click="removeParticipantId(id)">
                    &times;
                  </button>
                </span>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-2">
                <button class="btn btn-sm btn-outline-primary" :disabled="contactsLoading" @click="loadContacts(1)">
                  <i class="fa fa-refresh me-1" />
                  Refresh
                </button>
                <button class="btn btn-sm btn-outline-primary" :disabled="participantSuggestionsLoading"
                  @click="addParticipantsFromInput">
                  Apply filter
                </button>
              </div>
            </div>
          </div>
          <div class="card-body p-0">
            <div v-if="contactsLoading" class="py-4 text-center text-secondary">
              <i class="fa fa-spinner fa-spin me-2" />
              Loading contacts...
            </div>
            <div v-else-if="contactsError" class="alert alert-danger mx-3 my-3">
              {{ contactsError }}
            </div>
            <div v-else class="contact-list">
              <div v-if="!contacts.length" class="py-4 text-center text-secondary">
                No conversations found.
              </div>
              <div v-for="contact in contacts" :key="contact.key"
                class="contact-item d-flex align-items-start p-3 border-bottom cursor-pointer"
                :class="{ active: contact.key === selectedContactKey }" @click="selectContact(contact)">
                <div class="avatar-stack me-4">
                  <div class="avatar avatar-sm rounded-circle border border-white shadow-sm"
                    :class="{ placeholder: !contactPhotoUrl(contact, 0) }">
                    <img v-if="contactPhotoUrl(contact, 0)" :src="contactPhotoUrl(contact, 0)" class="rounded-circle" />
                    <span v-else class="text-xs text-white">
                      {{ getInitials(contactName(contact, 0)) }}
                    </span>
                  </div>
                  <div class="avatar avatar-sm rounded-circle border border-white shadow-sm"
                    :class="{ placeholder: !contactPhotoUrl(contact, 1) }">
                    <img v-if="contactPhotoUrl(contact, 1)" :src="contactPhotoUrl(contact, 1)" class="rounded-circle" />
                    <span v-else class="text-xs text-white">
                      {{ getInitials(contactName(contact, 1)) }}
                    </span>
                  </div>
                </div>
                <div class="flex-grow-1 min-width-0">
                  <div class="d-flex align-items-center mb-1">
                    <span class="fw-semibold text-sm me-2 text-truncate">
                      {{ contactName(contact, 0) }}
                    </span>
                    <span class="text-secondary text-xs px-1">•</span>
                    <span class="fw-semibold text-sm text-truncate">
                      {{ contactName(contact, 1) }}
                    </span>
                    <span class="badge bg-gradient-info ms-auto">
                      {{ contact.count }}
                    </span>
                  </div>
                  <div class="text-xs text-muted text-truncate">
                    {{ contact.lastMessagePreview }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer d-flex align-items-center justify-content-between"
            v-if="contactPagination.totalPages > 1 || contactPagination.hasNext">
            <button class="btn btn-sm btn-outline-secondary" :disabled="contactsLoading || contactPagination.page <= 1"
              @click="loadContacts(contactPagination.page - 1)">
              Prev
            </button>
            <span class="text-xs text-muted">
              Page {{ contactPagination.page }} / {{ contactPagination.totalPages }}
            </span>
            <button class="btn btn-sm btn-outline-secondary" :disabled="contactsLoading || !contactPagination.hasNext"
              @click="loadContacts(contactPagination.page + 1)">
              Next
            </button>
          </div>
        </div>
      </div>

      <div class="col-lg-8 mt-4 mt-lg-0">
        <div class="card h-100">
          <div class="card-header pb-0 d-flex align-items-center justify-content-between">
            <div>
              <h6 class="mb-0">Chat</h6>
              <p class="text-xs text-secondary mb-1">
                {{ participantLabel }}
              </p>
              <div v-if="selectedContact" class="d-flex flex-wrap align-items-center gap-1">
                <i v-if="relationsLoading" class="fa fa-spinner fa-spin text-xxs text-muted" />
                <span v-for="badge in relationBadges" :key="badge.key" class="badge text-uppercase text-xxs"
                  :style="relationTypeBadgeStyle(badge.type)">
                  {{ badge.label }}
                </span>
                <span v-if="!relationsLoading && !relationBadges.length" class="text-xxs text-muted">
                  No relation on record
                </span>
              </div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <div class="position-relative">
                <button class="btn btn-sm btn-outline-secondary" :disabled="translateLoading || !selectedContact"
                  @click="toggleTranslateMenu">
                  <i v-if="translateLoading" class="fa fa-spinner fa-spin me-1" />
                  <i v-else class="fa fa-language me-1" />
                  Translate
                </button>
                <div v-if="translateMenuOpen" class="list-group position-absolute shadow-sm translate-dropdown">
                  <button v-for="language in TRANSLATION_LANGUAGES" :key="language.code" type="button"
                    class="list-group-item list-group-item-action text-sm" @click="translateChat(language.code)">
                    {{ language.label }}
                  </button>
                </div>
              </div>
              <button class="btn btn-sm btn-outline-primary" :disabled="messagesLoading || !selectedContact"
                @click="loadMessages(1, true)">
                <i class="fa fa-refresh me-1" />
                Reload
              </button>
            </div>
          </div>
          <div v-if="translateError" class="alert alert-danger mx-3 mt-2 mb-0 py-2 text-xs">
            {{ translateError }}
          </div>
          <div class="card-body p-0">
            <div class="chat-wrapper overflow-auto" ref="messageContainer">
              <div v-if="!selectedContact"
                class="h-100 d-flex align-items-center justify-content-center text-secondary">
                Select a conversation to see messages.
              </div>
              <div v-else class="h-100 d-flex flex-column">
                <div class="chat-messages">
                  <div v-if="messagePagination.hasNext" class="d-flex justify-content-center my-2">
                    <button class="btn btn-sm btn-outline-secondary" :disabled="messagesLoading"
                      @click="loadOlderMessages">
                      <i v-if="messagesLoading" class="fa fa-spinner fa-spin me-1" />
                      Load older messages
                    </button>
                  </div>

                  <div v-if="messagesLoading && messages.length === 0" class="text-center text-secondary py-4">
                    <i class="fa fa-spinner fa-spin me-2" />
                    Loading messages...
                  </div>
                  <div v-else-if="messagesError" class="alert alert-danger mx-3 my-2">
                    {{ messagesError }}
                  </div>
                  <div v-else-if="!messages.length" class="text-center text-secondary py-4">
                    No messages yet.
                  </div>

                  <div v-for="message in messages" :key="message.id" :data-message-id="message.id"
                    class="d-flex align-items-start mb-3 px-3" :class="{ 'flex-row-reverse': !isFromUserA(message) }">
                    <div class="me-2 ms-2">
                      <router-link v-if="userProfileUrl(message.fromUserId)" :to="userProfileUrl(message.fromUserId)"
                        class="text-reset">
                        <div class="avatar avatar-sm rounded-circle border" :class="{
                          'bg-gradient-primary text-white': isFromUserA(message),
                          'bg-gradient-secondary text-white': !isFromUserA(message),
                        }">
                          <img v-if="getMediaUrl(firstPhotoPath(userMap[message.fromUserId]))"
                            :src="getMediaUrl(firstPhotoPath(userMap[message.fromUserId]))" class="rounded-circle" />
                          <span v-else class="text-xs d-flex align-items-center justify-content-center h-100 w-100">
                            {{ getInitials(resolveUserName(message.fromUserId)) }}
                          </span>
                        </div>
                      </router-link>
                      <div v-else class="avatar avatar-sm rounded-circle border" :class="{
                        'bg-gradient-primary text-white': isFromUserA(message),
                        'bg-gradient-secondary text-white': !isFromUserA(message),
                      }">
                        <img v-if="getMediaUrl(firstPhotoPath(userMap[message.fromUserId]))"
                          :src="getMediaUrl(firstPhotoPath(userMap[message.fromUserId]))" class="rounded-circle" />
                        <span v-else class="text-xs d-flex align-items-center justify-content-center h-100 w-100">
                          {{ getInitials(resolveUserName(message.fromUserId)) }}
                        </span>
                      </div>
                    </div>
                    <div class="flex-grow-1 min-width-0">
                      <div class="message-bubble" :class="{
                        'from-a': isFromUserA(message),
                        'from-b': !isFromUserA(message),
                        highlighted: highlightedMessageId === message.id,
                      }">
                        <div class="d-flex align-items-center mb-2">
                          <span class="fw-semibold text-sm me-2">
                            <router-link v-if="userProfileUrl(message.fromUserId)"
                              :to="userProfileUrl(message.fromUserId)"
                              class="text-reset text-decoration-none fw-semibold">
                              {{ resolveUserName(message.fromUserId) }}
                            </router-link>
                            <span v-else>
                              {{ resolveUserName(message.fromUserId) }}
                            </span>
                          </span>
                          <span v-if="message.deletedAt" class="badge bg-danger text-xxs me-2">Deleted</span>
                          <span class="text-xxs text-muted ms-auto">
                            {{ message.createdAtLabel }}
                          </span>
                        </div>

                        <div v-if="message.replyToMessage || message.replyToAtom" class="reply-chip mb-2">
                          <div v-if="message.replyToMessage" class="text-xxs text-muted mb-1">
                            Replying to: {{ replyToMessageSnippet(message) }}
                          </div>
                          <div v-if="message.replyToAtom" class="text-xxs text-muted">
                            Atom: {{ message.replyToAtom.attribute || 'atom' }} ·
                            {{ resolveUserName(message.replyToAtom.userId) }}
                          </div>
                        </div>

                        <div v-if="message.mediaKind === 'photo'" class="mb-2">
                          <img :src="getMediaUrl(message.mediaPath) || message.mediaPath" class="img-fluid rounded"
                            alt="Photo" />
                        </div>
                        <div v-else-if="message.mediaKind === 'voice'" class="mb-2">
                          <audio class="w-100" :src="getMediaUrl(message.mediaPath) || message.mediaPath" controls />
                        </div>
                        <div v-else-if="message.mediaKind === 'media'" class="mb-2">
                          <span class="badge bg-secondary">Attachment</span>
                        </div>

                        <p v-if="message.text" class="mb-0">{{ message.text }}</p>
                        <p v-if="translationFor(message)" class="mb-0 mt-1 pt-1 translation-block">
                          <span class="fw-semibold text-xxs">Translation: </span>{{ translationFor(message) }}
                        </p>

                        <div v-if="message.deletedAt || deliveredInfo(message) || seenInfo(message)"
                          class="text-xxs text-muted mt-1 pt-1 status-footer">
                          <span v-if="message.deletedAt">Deleted {{ message.deletedAtLabel }}</span>
                          <span v-if="message.deletedAt && (deliveredInfo(message) || seenInfo(message))"> · </span>
                          <span v-if="seenInfo(message)">Seen {{ seenInfo(message).dateTimeLabel }}</span>
                          <span v-else-if="deliveredInfo(message)">Delivered {{ deliveredInfo(message).dateTimeLabel
                            }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.contact-list {
  max-height: 70vh;
  overflow-y: auto;
}

.suggestion-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}

.min-width-0 {
  min-width: 0;
}

.contact-item {
  transition: background 0.15s ease;
}

.contact-item:hover {
  background: #f8f9fe;
}

.contact-item.active {
  background: #edf2ff;
}

.avatar-stack {
  display: grid;
  grid-template-columns: repeat(2, 1.25rem);
  gap: 0.3rem;
}

.avatar.placeholder {
  background: linear-gradient(135deg, #5e72e4, #11cdef);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-wrapper {
  min-height: 70vh;
  max-height: 80vh;
}

.chat-messages {
  overflow-y: auto;
  padding-top: 1rem;
  padding-bottom: 1rem;
  height: 100%;
}

.message-bubble {
  padding: 0.75rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.message-bubble.from-a {
  background: #f8f9fe;
  border: 1px solid #e9ecef;
}

.message-bubble.from-b {
  background: linear-gradient(135deg, #5e72e4, #825ee4);
  color: #fff;
}

.message-bubble.from-b .text-muted {
  color: rgba(255, 255, 255, 0.8) !important;
}

.message-bubble.highlighted {
  animation: message-highlight-fade 2.5s ease-out;
}

@keyframes message-highlight-fade {
  0% {
    box-shadow: 0 0 0 3px rgba(251, 99, 64, 0.9);
  }

  100% {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  }
}

.reply-chip {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
}

.message-bubble.from-b .reply-chip {
  background: rgba(255, 255, 255, 0.12);
}

.text-xxs {
  font-size: 0.7rem;
}

.translate-dropdown {
  top: 100%;
  right: 0;
  z-index: 1000;
  min-width: 140px;
}

.translation-block {
  font-size: 0.8rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.message-bubble.from-b .translation-block {
  border-top-color: rgba(255, 255, 255, 0.25);
}

.status-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.message-bubble.from-b .status-footer {
  border-top-color: rgba(255, 255, 255, 0.25);
}
</style>
